import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    EventTypes,
    PublicEventsService,
    type AuthStateResult,
    type OidcClientNotification,
} from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Socket } from 'socket.io-client';

import { AUTH_CONFIG_IDS } from '../../auth/auth.config';
import { DeviceStateUpdateExecutionInfo, ExecutionInfo } from '../../site-detail/models/Execution';
import { SiteId } from '../../site-detail/models/Site';
import { getUIDevicesSectionsSync, getUIDevicesStatesSync } from '../../site-detail/store/selectors/ui';
import { WEBSOCKET } from '../injectionTokens';
import {
    Actions,
    Event,
    SetExecutionsMessage,
    SetUserIdMessage,
    UISocketDataMessage,
    UISocketTextMessage,
} from '../models/UISocket';
import { User } from '../models/User';
import { AppState } from '../store/app-state';
import { getUserLoginState } from '../store/selectors/login';

import { SocketActionHandler, socketActionHandlersToken } from './SocketActionHandler';

@Injectable()
export class UISocketService {
    readonly userLogin$: Observable<User>;
    userLogin: User = null;

    readonly devicesStatesSync$: Observable<Array<DeviceStateUpdateExecutionInfo>>;
    devicesStatesSync: Array<DeviceStateUpdateExecutionInfo> = null;

    readonly devicesSectionsSync$: Observable<Array<ExecutionInfo>>;
    devicesSectionsSync: Array<ExecutionInfo> = null;

    private currentSite: SiteId | null = null;

    constructor(
        @Inject(WEBSOCKET) private readonly socket: Socket,
        @Inject(socketActionHandlersToken) private readonly handlers: SocketActionHandler[],
        private readonly store: Store<AppState>,
        private readonly authEvents: PublicEventsService,
    ) {
        this.userLogin$ = this.store.select(getUserLoginState);
        this.devicesStatesSync$ = this.store.select(getUIDevicesStatesSync);
        this.devicesSectionsSync$ = this.store.select(getUIDevicesSectionsSync);
    }

    initSocket(): void {
        this.socket.on(Event.CONNECT, () => {
            console.info('Connection established with server');

            if (this.userLogin?.id?.length) {
                this.setSessionUserId(this.userLogin.id);
            }

            if (this.devicesStatesSync?.length) {
                this.setExecutions(this.devicesStatesSync.map((el) => el.job_id));
            }

            if (this.devicesSectionsSync?.length) {
                this.setExecutions(this.devicesSectionsSync.map((el) => el.job_id));
            }

            if (this.currentSite) {
                this.emitCurrentSite();
            }
        });

        this.socket.on(Event.DISCONNECT, () => {
            console.info('Disconnected from server');
        });

        // Message Handlers
        this.socket.on('message', (message: UISocketTextMessage) => {
            console.info('Message received from server', message);
        });

        this.handlers.forEach((handler: SocketActionHandler) => {
            this.socket.on(handler.action, (message: UISocketDataMessage) => {
                handler.handle(message);
            });
        });

        // An authenticated socket ignores it; still sent for the back-end versions released before the handshake token.
        this.userLogin$.subscribe((userLogin) => {
            if (userLogin?.id?.length) {
                this.userLogin = userLogin;
                this.setSessionUserId(this.userLogin.id);
            }
        });

        this.devicesStatesSync$.subscribe((devicesStatesSync) => {
            this.devicesStatesSync = devicesStatesSync;
        });

        this.devicesSectionsSync$.subscribe((devicesSectionsSync) => {
            this.devicesSectionsSync = devicesSectionsSync;
        });

        // The server checks the site access with the token presented at connection: a renewed token is only
        // taken into account by reconnecting.
        this.authEvents
            .registerForEvents()
            .pipe(filter((event) => this.isInstallerTokenRenewal(event)))
            .subscribe(() => this.reconnect());

        this.socket.connect();
    }

    setSessionUserId(userId: string): void {
        const sessionIdMessage: SetUserIdMessage = {
            userId: userId,
        };
        this.socket.emit(Actions.SET_USER_ID, sessionIdMessage);
    }

    setExecutions(executions: string[]): void {
        const sessionSitesMessage: SetExecutionsMessage = {
            executions,
        };
        this.socket.emit(Actions.SET_EXECUTIONS, sessionSitesMessage);
    }

    setCurrentSite(site: SiteId): void {
        this.currentSite = site;
        this.emitCurrentSite();
    }

    private emitCurrentSite(): void {
        this.socket.emit(Actions.SET_CURRENT_SITE, { site: this.currentSite });
    }

    private reconnect(): void {
        this.socket.disconnect();
        this.socket.connect();
    }

    private isInstallerTokenRenewal(event: OidcClientNotification<unknown>): boolean {
        if (event.type !== EventTypes.NewAuthenticationResult) {
            return false;
        }

        const authState = event.value as AuthStateResult | undefined;
        return authState?.isAuthenticated === true && authState.configId === AUTH_CONFIG_IDS.INSTALLER;
    }
}
